<template>
  <!-- ==============================================================
       HowItWorksWidget — always-present "Guide" affordance with a
       PER-PAGE video.

       Fourth member of the floating right-edge widget family. Always a
       collapsed side-edge pill (portaled into App.vue's shared rail,
       order 4); clicking it opens a MODAL that plays the walkthrough
       MP4 *for the page the user is currently on*.

       Which video plays is decided by src/assets/js/guideVideos.js
       (route name → video basename). The widget loads
       public/videos/guides/<basename>.mp4 (+ optional <basename>.vtt
       captions and <basename>-poster.jpg). If a page's own file is
       missing it falls back to the DEFAULT_GUIDE video, then to a
       "coming soon" placeholder — so it never shows a broken player.

       ALL CSS here is UNSCOPED: the pill is portaled out of this
       component's DOM and the modal renders to a body portal (scoped
       CSS reaches neither). Class names are namespaced (.how-it-works-*).
       ============================================================== -->
  <div class="how-it-works-widget">

    <!-- ===== ALWAYS-ON PILL (portaled into the shared rail) ===== -->
    <portal to="guide-pill">
      <div class="how-it-works-pill"
           @click="openModal"
           role="button"
           tabindex="0"
           @keydown.enter.space.prevent="openModal"
           aria-label="Watch a video guide for this page"
           title="Watch a video guide for this page">
        <div class="how-it-works-pill-icon">
          <!-- Film strip ("movie") icon. -->
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 24 24"
               width="22" height="22"
               fill="none"
               stroke="currentColor"
               stroke-width="2"
               stroke-linecap="round"
               stroke-linejoin="round"
               aria-hidden="true">
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
            <line x1="7" y1="2" x2="7" y2="22"/>
            <line x1="17" y1="2" x2="17" y2="22"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <line x1="2" y1="7" x2="7" y2="7"/>
            <line x1="2" y1="17" x2="7" y2="17"/>
            <line x1="17" y1="17" x2="22" y2="17"/>
            <line x1="17" y1="7" x2="22" y2="7"/>
          </svg>
        </div>
        <div class="how-it-works-pill-label">GUIDE</div>
      </div>
    </portal>

    <!-- ===== VIDEO MODAL =====
         lazy so the MP4 only loads when opened (no background download on
         every page). modal-class namespaces the UNSCOPED styles below. -->
    <b-modal id="howItWorksModal"
             v-model="showModal"
             size="lg"
             scrollable
             lazy
             ok-only
             ok-title="Close"
             ok-variant="outline-dark"
             modal-class="how-it-works-modal"
             @hidden="onHidden">
      <template #modal-title>
        <span class="how-it-works-modal-title">
          <span class="how-it-works-modal-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 width="15" height="15"
                 fill="none"
                 stroke="currentColor"
                 stroke-width="2"
                 stroke-linecap="round"
                 stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
              <line x1="7" y1="2" x2="7" y2="22"/>
              <line x1="17" y1="2" x2="17" y2="22"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <line x1="2" y1="7" x2="7" y2="7"/>
              <line x1="2" y1="17" x2="7" y2="17"/>
              <line x1="17" y1="17" x2="22" y2="17"/>
              <line x1="17" y1="7" x2="22" y2="7"/>
            </svg>
          </span>
          {{ modalHeading }}
        </span>
      </template>

      <div class="how-it-works-body">

        <!-- ===== CATALOG — the global, chaptered "All Guides" menu =====
             Opened by the pill on EVERY page: browse the walkthrough VIDEOS for
             each part of the portal; the page you're on is flagged "You're here".
             Live tours + the admin Build button are page-contextual, so they live
             in the "On this page" bar (top), not on individual rows. -->
        <template v-if="showCatalog">
          <p class="how-it-works-lede hiw-catalog-lede">Walkthrough for each page of the portal</p>
          <div v-if="videoNote" class="hiw-video-note">{{ videoNote }}</div>
          <!-- one hidden picker, reused by "Import video" on any row -->
          <input ref="videoFile" type="file" accept="video/mp4,video/webm,video/*" class="hiw-video-input" @change="onVideoFile" />
          <div class="hiw-catalog">
            <section v-for="sec in catalog" :key="sec.title" class="hiw-cat-section">
              <h6 class="hiw-cat-title">{{ sec.title }}</h6>
              <ul class="hiw-cat-list">
                <li v-for="item in sec.items" :key="item.key">
                  <div class="hiw-cat-row"
                       :class="{ 'is-here': isHere(item), 'is-soon': !isReady(item), 'is-open': expandedKey === item.key }"
                       :role="rowClickable(item) ? 'button' : false"
                       :tabindex="rowClickable(item) ? 0 : false"
                       @click="onRowClick(item)"
                       @keydown.enter.space.prevent="onRowClick(item)">
                    <span class="hiw-cat-main">
                      <span class="hiw-cat-label">{{ item.label }}<span v-if="isHere(item)" class="hiw-cat-here">You’re here</span></span>
                      <span class="hiw-cat-blurb">{{ item.blurb }}</span>
                    </span>
                    <span v-if="isReady(item)" class="how-it-works-choice-badge hiw-badge-available">Available</span>
                    <span v-else class="how-it-works-choice-badge">{{ comingSoonLabel(item) }}</span>
                    <span v-if="rowClickable(item)" class="how-it-works-choice-play" aria-hidden="true">{{ expandedKey === item.key ? "▾" : ((isMasterUser || hasTour(item)) ? "▸" : "▶") }}</span>
                  </div>
                  <!-- Choices revealed on click. A Master sees View / Import / Export
                       video + Create a Live Tutorial; customers see video / take-tour. -->
                  <div v-if="expandedKey === item.key" class="hiw-cat-actions">
                    <button v-if="canViewVideo(item)" type="button" class="hiw-act hiw-act-ghost" @click="selectItem(item)">▶ View video</button>
                    <button v-else-if="isMasterUser" type="button" class="hiw-act hiw-act-rec" @click="createVideo(item)">🎬 New Video</button>
                    <button v-if="canTakeTour(item)" type="button" class="hiw-act hiw-act-primary" @click="takeTour(item)">▶ Take the live tour</button>
                    <button v-if="isMasterUser" type="button" class="hiw-act hiw-act-rec" @click="createTutorial(item)">✎ Create a Live Tutorial</button>
                    <button v-if="isMasterUser" type="button" class="hiw-act hiw-act-ghost" @click="importVideo(item)">⬆ Import video</button>
                    <button v-if="isMasterUser && hasVideo(item)" type="button" class="hiw-act hiw-act-ghost" @click="exportVideo(item)">⬇ Export video</button>
                    <button v-if="isMasterUser && hasUploadedVideo(item)" type="button" class="hiw-act hiw-act-del" @click="deleteVideo(item)">🗑 Delete video</button>
                  </div>
                  <ul v-if="item.children && item.children.length" class="hiw-cat-children">
                    <li v-for="c in item.children" :key="c.key">
                      <div class="hiw-cat-row hiw-cat-child"
                           :class="{ 'is-here': isHere(c), 'is-soon': !isReady(c), 'is-open': expandedKey === c.key }"
                           :role="rowClickable(c) ? 'button' : false"
                           :tabindex="rowClickable(c) ? 0 : false"
                           @click="onRowClick(c)"
                           @keydown.enter.space.prevent="onRowClick(c)">
                        <span class="hiw-cat-main">
                          <span class="hiw-cat-label">{{ c.label }}<span v-if="isHere(c)" class="hiw-cat-here">You’re here</span></span>
                          <span class="hiw-cat-blurb">{{ c.blurb }}</span>
                        </span>
                        <span v-if="isReady(c)" class="how-it-works-choice-badge hiw-badge-available">Available</span>
                        <span v-else class="how-it-works-choice-badge">{{ comingSoonLabel(c) }}</span>
                        <span v-if="rowClickable(c)" class="how-it-works-choice-play" aria-hidden="true">{{ expandedKey === c.key ? "▾" : ((isMasterUser || hasTour(c)) ? "▸" : "▶") }}</span>
                      </div>
                      <div v-if="expandedKey === c.key" class="hiw-cat-actions">
                        <button v-if="canViewVideo(c)" type="button" class="hiw-act hiw-act-ghost" @click="selectItem(c)">▶ View video</button>
                        <button v-else-if="isMasterUser" type="button" class="hiw-act hiw-act-rec" @click="createVideo(c)">🎬 New Video</button>
                        <button v-if="canTakeTour(c)" type="button" class="hiw-act hiw-act-primary" @click="takeTour(c)">▶ Take the live tour</button>
                        <button v-if="isMasterUser" type="button" class="hiw-act hiw-act-rec" @click="createTutorial(c)">✎ Create a Live Tutorial</button>
                        <button v-if="isMasterUser" type="button" class="hiw-act hiw-act-ghost" @click="importVideo(c)">⬆ Import video</button>
                        <button v-if="isMasterUser && hasVideo(c)" type="button" class="hiw-act hiw-act-ghost" @click="exportVideo(c)">⬇ Export video</button>
                        <button v-if="isMasterUser && hasUploadedVideo(c)" type="button" class="hiw-act hiw-act-del" @click="deleteVideo(c)">🗑 Delete video</button>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </section>
          </div>
        </template>

        <!-- ===== PLAYER — a chosen walkthrough video ===== -->
        <template v-else>
          <button type="button" class="how-it-works-back" @click="backToList">‹ All guides</button>

          <p class="how-it-works-lede" v-if="!activeComingSoon">{{ playerLede }}</p>

          <!-- Checking the backend for a recorded video; hold the player so we don't
               flash/mount the static MP4 first (which then swaps + mis-fires errors). -->
          <div class="how-it-works-video-loading" v-if="resolvingVideo && !activeComingSoon">
            Loading the video…
          </div>

          <div class="how-it-works-video-wrap" v-else-if="!videoError && !activeComingSoon">
            <!-- :key remounts the <video> when the basename changes. -->
            <video :key="playerKey"
                   ref="video"
                   class="how-it-works-video"
                   controls
                   controlslist="nodownload"
                   preload="metadata"
                   playsinline
                   :poster="posterSrc"
                   :src="videoSrc"
                   @error="onVideoError">
              <track v-if="!recordedSrc"
                     kind="captions"
                     :src="captionsSrc"
                     srclang="en"
                     label="English"
                     default />
              Your browser doesn't support embedded video.
            </video>
          </div>

          <div class="how-it-works-video-missing" v-else-if="activeComingSoon">
            This walkthrough is coming soon. Check back shortly.
          </div>

          <div class="how-it-works-video-missing" v-else>
            A video guide for this part isn't available yet. Please check back soon.
          </div>
        </template>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { GUIDE_VIDEOS, GUIDE_CATALOG, DEFAULT_GUIDE } from "@/assets/js/guideVideos";
import FeApiService from "@/assets/js/FeApiService";

var feapi = new FeApiService();

// Same-origin static assets in the app's public/ folder. BASE_URL is the
// app's deploy base (usually "/"), injected by Vue CLI at build time.
var BASE =
  (typeof process !== "undefined" && process.env && process.env.BASE_URL) ||
  "/";
const GUIDE_BASE = BASE + "videos/guides/";

export default {
  name: "HowItWorksWidget",

  data() {
    return {
      showModal: false,
      // Basename currently loaded into the <video>. Set to the page's
      // basename on open; swapped to DEFAULT_GUIDE if the page file is
      // missing (see onVideoError).
      activeBasename: DEFAULT_GUIDE || "how-it-works",
      // True once even the fallback video failed — drives the placeholder.
      videoError: false,
      // ----- catalog state -----
      // The pill opens the global chaptered catalog (showCatalog=true); picking a
      // walkthrough flips to the player (false) and backToList returns to it.
      showCatalog: true,
      // Catalog row whose action choices (View video / Take tour / Create a Live
      // Tutorial / Import-Export video) are currently revealed. "" = none expanded.
      expandedKey: "",
      // Flow keys with an uploaded/recorded video on the server (so a page with no
      // static MP4 still shows "View video"). Refreshed on open for coming-soon pages.
      savedVideoFlows: [],
      // Transient status line for video import/export feedback.
      videoNote: "",
      // The chosen item is a not-yet-produced walkthrough → show a
      // "coming soon" note instead of the <video>.
      activeComingSoon: false,
      // Label of the chosen group item (for the modal title / lede).
      activeLabel: "",
      // When a flow has an admin-recorded "Preview tour" video, its absolute URL
      // goes here and PLAYS INSTEAD of the static <basename>.mp4. Cleared on every
      // (re)selection; populated async by selectItem for items with a `tour` flow.
      recordedSrc: "",
      recordedReq: 0,   // token to ignore stale guideVideoInfo responses. MUST NOT start with "_": Vue 2 doesn't proxy _-prefixed data props onto `this`, so `this._recordedReq` was undefined → ++ gave NaN → the `token !== this._x` guard was NaN!==NaN (always true) → recorded video never applied + loading stuck.
      // While we check the backend for a recorded video, hold off rendering the
      // <video> so we DON'T mount the static MP4 first and then swap (that swap
      // tore down the static element mid-load, firing a spurious error that cleared
      // the freshly-set recordedSrc → bounced back to the static video).
      resolvingVideo: false,
      // Flow keys that have a saved/cached live tour (DB + localStorage). Drives
      // the per-page "Take the interactive tour" button. Refreshed on modal open.
      savedTourFlows: [],
    };
  },

  computed: {
    ...mapGetters({ currentUser: "currentUser" }),
    // Master/admin detection (mirrors the other widgets). Respects the
    // header "view as role" preview, so a Master previewing as a customer
    // won't see the admin-only Record button.
    isMasterUser() {
      var u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      var r = (u.role || "").toString().toLowerCase();
      return r === "master" || r === "admin";
    },
    // The basename the CURRENT page wants. Resolution order:
    //   1. explicit mapping by route NAME (guideVideos.js)
    //   2. explicit mapping by lower-cased URL path (convenience)
    //   3. convention: derive from the route's URL (slashes → dashes)
    //   4. the DEFAULT_GUIDE
    pageBasename() {
      var r = this.$route || {};
      if (r.name && GUIDE_VIDEOS[r.name]) return GUIDE_VIDEOS[r.name];
      var path = ((r.path || "").split("?")[0].split("#")[0]).toLowerCase();
      if (GUIDE_VIDEOS[path]) return GUIDE_VIDEOS[path];
      var slug = this._routeSlug(r);
      return slug || DEFAULT_GUIDE || "how-it-works";
    },
    // Friendly page label for the modal title (e.g. "Create New Order").
    pageLabel() {
      var r = this.$route || {};
      return (r.meta && r.meta.breadcrumb) || r.name || "This Page";
    },
    videoSrc() {
      // An admin-recorded tour video (if any) wins over the static MP4.
      return this.recordedSrc || (GUIDE_BASE + this.activeBasename + ".mp4");
    },
    captionsSrc() {
      return GUIDE_BASE + this.activeBasename + ".vtt";
    },
    posterSrc() {
      // The recorded WebM has no companion poster; let the video show its first frame.
      return this.recordedSrc ? "" : (GUIDE_BASE + this.activeBasename + "-poster.jpg");
    },
    // Re-key the <video> on source CHANGE (page nav, list pick, fallback, or a
    // recorded URL arriving async) so the element reloads the new src.
    playerKey() {
      return this.activeBasename + "|" + (this.recordedSrc ? "rec" : "mp4");
    },
    // The global chaptered catalog (sections → items → child chapters).
    catalog() { return GUIDE_CATALOG; },
    // Friendly label for the page the user is currently on — the matching catalog
    // item if any, else the route's breadcrumb/name. Shown in the "On this page" bar.
    herePageLabel() {
      var self = this, found = "";
      (this.catalog || []).forEach(function (sec) {
        (sec.items || []).forEach(function (it) {
          if (self.isHere(it)) found = found || it.label;
          (it.children || []).forEach(function (c) { if (self.isHere(c)) found = found || c.label; });
        });
      });
      return found || this.pageLabel;
    },
    // Modal title: the catalog heading, or the chosen walkthrough's name.
    modalHeading() {
      if (this.showCatalog) return "Portal Guides";
      return (this.activeLabel || this.pageLabel) + " — Guide";
    },
    playerLede() {
      if (this.activeLabel) return "A quick walkthrough of the " + this.activeLabel + " flow.";
      return "A quick walkthrough of this page.";
    },
    // Stable per-page flow key for a page that ISN'T a multi-walkthrough group
    // (those use their own sub-flow keys, e.g. order/quote). The route NAME is
    // preferred (unique + readable in the DB); the URL slug is the fallback. This
    // is the key the builder saves under and playback loads — same on every screen.
    pageFlow() {
      var r = this.$route || {};
      // Search hosts 3 flows behind one route via ?intent — map to that flow key
      // (order / quote / search) so the right tour is taken/built from the pill.
      if (r.name === "Search") {
        var intent = ((r.query && r.query.intent) || "").toString().toLowerCase();
        return (intent === "order" || intent === "quote") ? intent : "search";
      }
      return r.name || this._routeSlug(r) || "page";
    },
    // True when a live tour has been built for THIS page (so customers see a
    // "Take the tour" button in the "On this page" bar).
    hasPageTour() {
      return this.savedTourFlows.indexOf(this.pageFlow) >= 0;
    },
  },

  methods: {
    openModal() {
      this.videoError = false;
      this.recordedSrc = "";
      this.resolvingVideo = false;
      this.recordedReq++;   // invalidate any in-flight recorded-video check
      this._refreshTourFlows();   // so "Take the live tour" reflects the latest saved tours
      this._refreshVideoFlows();  // so coming-soon pages with an uploaded video show "View video"
      // Always open the global catalog; the page you're on is flagged "You're here".
      this.showCatalog = true;
      this.expandedKey = "";
      this.activeComingSoon = false;
      this.activeLabel = "";
      this.videoNote = "";
      this.showModal = true;
    },
    // Play (or show "coming soon" for) a chosen walkthrough from the catalog.
    selectItem(item) {
      this.showCatalog = false;
      this.activeLabel = item.label || "";
      // A page with a static MP4 (or an uploaded video) is playable; only a
      // coming-soon page with NO uploaded video shows the "coming soon" note.
      this.activeComingSoon = !this.hasVideo(item);
      this.activeBasename = item.basename || DEFAULT_GUIDE || "how-it-works";
      this.recordedSrc = "";
      this.videoError = false;
      // Prefer an uploaded/recorded video for THIS page (keyed by its flow) over
      // the static MP4 — so an imported video plays for any page in the catalog.
      // Resolve it FIRST (player held until then) so we never mount the static MP4
      // and then swap to the recording.
      var flow = this.tourFlowFor(item);
      this.resolvingVideo = !!flow;
      this._maybeUseRecorded(flow);
    },
    // Async: pick the recorded tour video for `flow` when the backend has one, else
    // keep the static MP4. Resolves resolvingVideo so the player renders ONCE with
    // the final source. Token-guarded so a slow response can't clobber a newer pick.
    _maybeUseRecorded(flow) {
      if (!flow) { this.resolvingVideo = false; return; }
      var self = this;
      var token = ++this.recordedReq;
      feapi.guideVideoInfo(flow).then(function (info) {
        if (token !== self.recordedReq) return;   // a newer selection owns the player now
        if (info && info.exists) { self.recordedSrc = feapi.getGuideVideoUrl(flow); self.activeComingSoon = false; }
        self.resolvingVideo = false;
      }).catch(function () { if (token === self.recordedReq) self.resolvingVideo = false; });   // no recorded video → static MP4
    },
    // Return to the catalog (stops any playing video first).
    backToList() {
      this.onHidden();
      this.recordedSrc = "";
      this.resolvingVideo = false;
      this.recordedReq++;
      this.showCatalog = true;
      this.expandedKey = "";
    },
    // Does the current route correspond to this catalog item? (route name +, where
    // a page multiplexes flows by query, the ?intent / ?tab). Drives "You're here".
    isHere(item) {
      var r = this.$route || {};
      if (!item || r.name !== item.routeName) return false;
      var q = r.query || {};
      if (item.intent != null) return (q.intent || "").toString().toLowerCase() === item.intent;
      if (item.tab) return ((q.tab || "").toString().toLowerCase() || "orders") === item.tab;
      return true;
    },
    // The tour flow key for a catalog item — matches the destination page's
    // pageFlow so the builder saves under, and playback loads, the same key
    // (Search items carry an explicit order/quote/search flow; others use the
    // route name).
    tourFlowFor(item) { return (item && (item.tour || item.routeName)) || ""; },
    // Has a live tour been saved for this item's page?
    hasTour(item) { return this.savedTourFlows.indexOf(this.tourFlowFor(item)) >= 0; },
    // A page is customer-READY (no "Coming soon", customer can use it) ONLY when it
    // has BOTH a recorded video AND an interactive live tour. Until both exist it
    // stays "Coming soon" and a customer gets no actions (per user: Coming soon only
    // clears when there's an interactive tutorial PLUS a video).
    isReady(item) { return this.hasVideo(item) && this.hasTour(item); },
    // Badge under a not-ready row. A Master who already has ONE half can PREVIEW it,
    // so label it "Preview · <other> pending" rather than the customer-facing "Coming
    // soon" (which contradicts the fact that the Master can still play it).
    comingSoonLabel(item) {
      if (this.isMasterUser) {
        var ht = this.hasTour(item), hv = this.hasVideo(item);
        if (ht && !hv) return "Preview · video pending";
        if (hv && !ht) return "Preview · tour pending";
      }
      return "Coming soon";
    },
    // Customer sees "View video" / "Take the live tour" ONLY when the page is ready
    // (both pieces exist). A Master sees each as soon as its own piece exists, so they
    // can preview the video / test the tour while still building the other half.
    canViewVideo(item) { return this.hasVideo(item) && (this.isMasterUser || this.hasTour(item)); },
    canTakeTour(item) { return this.hasTour(item) && (this.isMasterUser || this.hasVideo(item)); },
    // A page is "ready" (shows a video) ONLY when it has an UPLOADED/recorded video
    // on the server — the recorded live tutorial is the source of truth. Built-in/
    // static MP4s no longer count as the guide video, so EVERY page reads "Coming
    // soon" until a tutorial is recorded (or a video imported). Same check as
    // hasUploadedVideo — both gate off the server's per-flow video.
    hasVideo(item) {
      if (!item) return false;
      return this.savedVideoFlows.indexOf(this.tourFlowFor(item)) >= 0;
    },
    // Is the row interactive? Masters always have actions (build / import); a
    // customer only when the page is READY (both a video AND a live tour).
    rowClickable(item) {
      return this.isMasterUser || this.isReady(item);
    },
    // Click a catalog row. Master → always reveal the choices. Customer → locked
    // until ready; once ready (video + tour) reveal both choices.
    onRowClick(item) {
      if (this.isMasterUser) { this.expandedKey = this.expandedKey === item.key ? "" : item.key; return; }
      if (!this.isReady(item)) return;   // customers: nothing actionable until a video + tour both exist
      this.expandedKey = this.expandedKey === item.key ? "" : item.key;
    },
    // Find which pages have an UPLOADED/recorded video on the server (keyed by
    // flow). Drives coming-soon pages showing "View video" + the admin Delete/Export
    // buttons. A few cheap per-flow stats on modal open (de-duped — the Order
    // History tabs share one flow).
    _refreshVideoFlows() {
      var self = this;
      this.savedVideoFlows = [];
      var flows = [];
      (this.catalog || []).forEach(function (sec) {
        (sec.items || []).forEach(function (it) {
          flows.push(self.tourFlowFor(it));
          (it.children || []).forEach(function (c) { flows.push(self.tourFlowFor(c)); });
        });
      });
      var uniq = [];
      flows.forEach(function (f) { if (f && uniq.indexOf(f) < 0) uniq.push(f); });
      uniq.forEach(function (flow) {
        feapi.guideVideoInfo(flow).then(function (info) {
          if (info && info.exists && self.savedVideoFlows.indexOf(flow) < 0) self.savedVideoFlows.push(flow);
        });
      });
    },
    // True when an UPLOADED/recorded video exists for this page (deletable; distinct
    // from a built-in static MP4, which can't be removed at runtime).
    hasUploadedVideo(item) { return this.savedVideoFlows.indexOf(this.tourFlowFor(item)) >= 0; },
    // Admin: delete the UPLOADED video → the page reverts to its built-in MP4 (or
    // "coming soon"). Never touches the static MP4 (a build asset).
    deleteVideo(item) {
      var self = this;
      var flow = this.tourFlowFor(item);
      var label = item.label || flow;
      var after = item.comingSoon ? "This page will go back to “coming soon.”" : "This page will fall back to its built-in video.";
      if (!window.confirm("Delete the uploaded “" + label + "” video? " + after)) return;
      this.videoNote = "Deleting the “" + label + "” video…";
      feapi.deleteGuideVideo(flow).then(function () {
        var i = self.savedVideoFlows.indexOf(flow); if (i >= 0) self.savedVideoFlows.splice(i, 1);
        self.recordedReq++;   // invalidate any cached recorded-video check
        self.recordedSrc = "";
        self.videoNote = "✓ Deleted the “" + label + "” video.";
      }).catch(function () { self.videoNote = "✕ Couldn't delete that video. Please try again."; });
    },
    // Admin: upload a video file as THIS page's guide video — stored server-side,
    // keyed by the page's flow, no deploy. Reuses the one hidden file input.
    importVideo(item) {
      this._videoTarget = { flow: this.tourFlowFor(item), label: item.label || item.key };
      var inp = this.$refs.videoFile;
      if (inp) { inp.value = ""; inp.click(); }
    },
    onVideoFile(e) {
      var self = this;
      var inp = e && e.target;
      var file = inp && inp.files && inp.files[0];
      var t = this._videoTarget;
      if (!file || !t) return;
      this.videoNote = "Uploading the “" + t.label + "” video…";
      feapi.saveGuideVideo(t.flow, file).then(function () {
        if (self.savedVideoFlows.indexOf(t.flow) < 0) self.savedVideoFlows.push(t.flow);
        self.recordedReq++;   // so the next play picks up the new video
        self.videoNote = "✓ Video saved for “" + t.label + "” — it now plays here. Export it to move it to another environment.";
      }).catch(function () {
        self.videoNote = "✕ Couldn't upload that video. Make sure it's an MP4 or WebM and try again.";
      });
      try { inp.value = ""; } catch (x) {}
    },
    // Admin: download THIS page's video to a file (the uploaded one if present, else
    // the static MP4) so you can Import it into another environment.
    exportVideo(item) {
      var self = this;
      var flow = this.tourFlowFor(item);
      var label = item.label || flow;
      feapi.guideVideoInfo(flow).then(function (info) {
        if (info && info.exists) self._download(feapi.getGuideVideoUrl(flow), flow);
        else if (!item.comingSoon) self._download(GUIDE_BASE + item.basename + ".mp4", flow);
        else self.videoNote = "No video to export for “" + label + "” yet — import or record one first.";
      });
    },
    _download(url, flowName) {
      var self = this;
      fetch(url).then(function (r) { return r.blob(); }).then(function (blob) {
        var ext = (blob.type && blob.type.indexOf("webm") >= 0) ? "webm" : "mp4";
        var fname = "guide-video-" + String(flowName).replace(/[^a-z0-9_-]+/gi, "-") + "." + ext;
        var u = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = u; a.download = fname;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(u); }, 1000);
        self.videoNote = "✓ Downloaded " + fname + " — Import it on the other environment.";
      }).catch(function () { self.videoNote = "✕ Couldn't download that video."; });
    },
    // Take a page's live tour: go to that page first (so the tour runs on the real
    // screen), then start it.
    takeTour(item) { this._gotoThenEmit(item, "start-guide-tour"); },
    // Admin: build/edit a live tour for a page — navigate there, then open the
    // builder (which drives that page). This is how you "create a tour on each page".
    createTutorial(item) { this._gotoThenEmit(item, "open-tour-builder"); },
    // Admin: "New Video" for a page with no recording yet — same as Create a Live
    // Tutorial, but flags the builder to jump straight to the record-mode picker
    // (audio / no-audio) once its steps load, so it "starts at step 1" recording.
    createVideo(item) {
      var flow = this.tourFlowFor(item);
      if (!flow) return;
      try { localStorage.setItem("jcs_tour_autorecord", flow); } catch (e) { /* no-op */ }
      this._gotoThenEmit(item, "open-tour-builder");
    },
    _gotoThenEmit(item, evt) {
      var flow = this.tourFlowFor(item);
      if (!flow) return;
      this.expandedKey = "";
      this.showModal = false;
      var self = this;
      var fire = function () {
        self.$nextTick(function () { setTimeout(function () { self.$root.$emit(evt, flow); }, 350); });
      };
      // Already on the right page? just fire. Otherwise navigate there first
      // (by route name + the page's ?intent/?tab), then fire.
      if (this.$route.name === item.routeName) { fire(); return; }
      var target = { name: item.routeName };
      if (item.intent) target.query = { intent: item.intent };
      else if (item.tab) target.query = { tab: item.tab };
      try {
        var p = this.$router.push(target);
        if (p && typeof p.then === "function") p.then(fire).catch(function () { fire(); });
        else fire();
      } catch (e) { fire(); }
    },
    // Per-page live tour — play it / build-edit it. Flow = pageFlow,
    // so ANY page can host its own tour (not just the GUIDE_GROUPS pages).
    startPageTour() { this._emitFlow("start-guide-tour", this.pageFlow); },
    startPageBuilder() { this._emitFlow("open-tour-builder", this.pageFlow); },
    // Close this modal (so its backdrop is gone), then hand off to GuideTourWidget /
    // GuideTourBuilder which drive the real page. Shared by every tour/build launch.
    _emitFlow(evt, flow) {
      if (!flow) return;
      this.showModal = false;
      var self = this;
      this.$nextTick(function () {
        setTimeout(function () { self.$root.$emit(evt, flow); }, 280);
      });
    },
    // Refresh which flows have a saved tour (DB authoritative; localStorage drafts
    // counted as a fallback so the button still works if the tour backend is down).
    _refreshTourFlows() {
      var self = this;
      var localFlows = function () {
        var out = [];
        try {
          for (var i = 0; i < localStorage.length; i++) {
            var k = localStorage.key(i);
            if (k && k.indexOf("jcs_tour_rec_") === 0) out.push(k.slice(13));
          }
        } catch (e) {}
        return out;
      };
      feapi.getGuideTours().then(function (d) {
        var flows = Object.keys(d || {});
        localFlows().forEach(function (f) { if (flows.indexOf(f) < 0) flows.push(f); });
        self.savedTourFlows = flows;
      }).catch(function () { self.savedTourFlows = localFlows(); });
    },
    onVideoError(e) {
      // Ignore a stray error from a torn-down/previous <video> (an element removed
      // during a source swap fires a spurious abort) — only act on the element
      // that's actually on screen, so it can't wrongly clear a valid recordedSrc.
      if (e && e.target && this.$refs.video && e.target !== this.$refs.video) return;
      // A recorded tour video that won't load (e.g. it was deleted) → fall back
      // to the static MP4 for this page before the default-overview fallback.
      if (this.recordedSrc) { this.recordedSrc = ""; return; }
      // The current file failed to load. Try the default overview video
      // once; if that's missing too, show the placeholder. Changing
      // activeBasename re-keys the <video> so it reloads the new source.
      if (DEFAULT_GUIDE && this.activeBasename !== DEFAULT_GUIDE) {
        this.activeBasename = DEFAULT_GUIDE;
      } else {
        this.videoError = true;
      }
    },
    onHidden() {
      // Stop playback when the modal closes so nothing keeps playing in
      // the background. With `lazy` the content is torn down anyway; this
      // is a cheap, safe belt-and-suspenders.
      var v = this.$refs.video;
      if (v && typeof v.pause === "function") {
        try {
          v.pause();
          v.currentTime = 0;
        } catch (e) {
          /* no-op */
        }
      }
    },
    // Convention fallback for pages not listed in guideVideos.js: derive a
    // basename from the route's definition path, dropping :params and
    // turning slashes into dashes. e.g. /orders/shopassembly →
    // "orders-shopassembly"; /account/orderdetails/:id → "account-orderdetails".
    _routeSlug(r) {
      var def = "";
      if (r && r.matched && r.matched.length) {
        def = r.matched[r.matched.length - 1].path || "";
      }
      if (!def) def = (r && r.path) || "";
      return String(def)
        .replace(/:[^/]+/g, "")
        .replace(/[^a-zA-Z0-9/]+/g, "-")
        .replace(/\/+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
    },
  },
};
</script>

<style>
/* ============================================================
   COLLAPSED PILL — UNSCOPED.
   The pill is portaled into App.vue's .floating-pill-rail (outside this
   component's DOM), so scoped CSS can't reach it. Namespaced
   (.how-it-works-pill*) so these global rules are safe. Positioning is
   owned by the rail; the pill is a static flex child.
   ============================================================ */
.how-it-works-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  min-width: 56px;
  width: 56px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}
.how-it-works-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}
.how-it-works-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.how-it-works-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.how-it-works-pill-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  line-height: 1;
  white-space: nowrap;
}

/* ============================================================
   VIDEO MODAL — UNSCOPED (b-modal renders to a body portal).
   Namespaced under .how-it-works-modal (the modal-class).
   ============================================================ */
.how-it-works-modal .modal-dialog {
  max-width: 820px;
  z-index: 10050 !important;
  /* Pin the modal BELOW the sticky header and cap its height so the long catalog
     scrolls INSIDE it — otherwise a centered modal taller than the viewport runs
     up behind the z-9200 sticky header and its title/top get cut off (worse the
     more you zoom in / the shorter the window). --jcs-sticky-h is the live header
     height (App.vue keeps it updated); the fallback covers first paint. NOT
     `centered` — top-anchored so it always clears the header. */
  margin-top: calc(var(--jcs-sticky-h, 175px) + 10px);
  max-height: calc(100vh - var(--jcs-sticky-h, 175px) - 28px);
}
/* Also lift above the header's stacking context (matches Cart's modify-order-modal). */
.how-it-works-modal.modal { z-index: 10050 !important; }
.how-it-works-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2a2a2a;
}
.how-it-works-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
}
.how-it-works-body {
  padding: 0.1rem 0.15rem;
}
.how-it-works-lede {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0.75rem;
}
.how-it-works-video-wrap {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
}
.how-it-works-video {
  display: block;
  width: 100%;
  height: auto;
  max-height: 70vh;
  background: #000;
}
.how-it-works-video-missing {
  padding: 2.5rem 1rem;
  text-align: center;
  font-style: italic;
  color: #777;
  background: #f7f7f7;
  border: 1px dashed #ddd;
  border-radius: 6px;
}
.how-it-works-video-loading {
  padding: 2.5rem 1rem;
  text-align: center;
  font-style: italic;
  color: #777;
  background: #f7f7f7;
  border: 1px dashed #ddd;
  border-radius: 6px;
}
/* Per-page live-tour actions under the video (reuses .hiw-act* button styles). */
.how-it-works-tour-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eee;
}

/* ===== GLOBAL CATALOG — the chaptered "All Guides" menu ===== */
.hiw-here-bar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  padding: 10px 12px; margin-bottom: 14px;
  background: #fbf3f2; border: 1px solid #efd9d6; border-radius: 8px;
}
.hiw-here-label { font-size: 12px; font-weight: 700; color: #c0392b; flex: 1 1 auto; }
.hiw-here-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.hiw-catalog { display: flex; flex-direction: column; gap: 16px; }
/* Catalog header line — centered, larger, bold, brand red (only in the catalog;
   the shared .how-it-works-lede keeps its default look in the video player). */
.hiw-catalog-lede {
  text-align: center; font-size: 16px; font-weight: 800; color: #c0392b; margin-bottom: 1rem;
}
/* Section/category headers — red + bold. */
.hiw-cat-title {
  margin: 0 0 7px; font-size: 12px; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase; color: #c0392b;
}
.hiw-cat-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.hiw-cat-children {
  list-style: none; margin: 8px 0 0; padding: 0 0 0 18px;
  display: flex; flex-direction: column; gap: 8px; border-left: 2px solid #efe3e1;
}
.hiw-cat-row {
  display: flex; align-items: center; gap: 12px; width: 100%;
  background: #fff; border: 1px solid #e4e4e4; border-left: 3px solid #c0392b;
  border-radius: 8px; padding: 11px 14px; cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}
.hiw-cat-row:hover { background: #fafafa; transform: translateX(2px); box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08); }
.hiw-cat-row:focus-visible { outline: 2px solid #c0392b; outline-offset: 2px; }
.hiw-cat-child { border-left-color: #d9b8b4; padding: 9px 12px; }
.hiw-cat-row.is-soon { cursor: default; border-left-color: #d8d8d8; opacity: 0.85; }
.hiw-cat-row.is-soon:hover { background: #fff; transform: none; box-shadow: none; }
.hiw-cat-row.is-here { background: #fbf3f2; border-color: #e2b8b3; }
.hiw-cat-row.is-open { background: #fafafa; border-color: #c0392b; }
/* Choice buttons revealed under a clicked row (View video / Take tour / Create). */
.hiw-cat-actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0 4px; padding-left: 14px; }
.hiw-cat-children .hiw-cat-actions { padding-left: 14px; }
.hiw-video-input { display: none; }
.hiw-video-note {
  margin: 0 0 12px; padding: 8px 12px; font-size: 12.5px; font-weight: 600;
  color: #2a2a2a; background: #f3f3f3; border: 1px solid #e2e2e2; border-radius: 6px;
}
.hiw-cat-main { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
.hiw-cat-label { font-weight: 700; font-size: 14px; color: #2a2a2a; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hiw-cat-blurb { font-size: 12px; color: #666; line-height: 1.35; }
.hiw-cat-here {
  font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: #1f6e23; background: #e7f5e8; border: 1px solid #b6e0b9; border-radius: 999px; padding: 1px 7px;
}

/* ===== CHOOSER LIST (multi-walkthrough pages, e.g. Search) ===== */
.how-it-works-chooser {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.how-it-works-choice {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-left: 3px solid #c0392b;
  border-radius: 8px;
  padding: 13px 16px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}
.how-it-works-choice:hover {
  background: #fafafa;
  transform: translateX(2px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
.how-it-works-choice:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.how-it-works-choice-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 auto;
}
.how-it-works-choice-label {
  font-weight: 700;
  font-size: 15px;
  color: #2a2a2a;
}
.how-it-works-choice-blurb {
  font-size: 12.5px;
  color: #666;
  line-height: 1.35;
}
.how-it-works-choice-play {
  flex: 0 0 auto;
  color: #c0392b;
  font-size: 16px;
}
.how-it-works-choice-badge {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #b9770e;
  background: #fff4e0;
  border: 1px solid #f0c36d;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
}
/* Green "Available" pill — pages that have BOTH a video and a live tutorial. */
.how-it-works-choice-badge.hiw-badge-available {
  color: #1f6e23;
  background: #e7f5e8;
  border-color: #b6e0b9;
}
.how-it-works-back {
  border: 0;
  background: none;
  padding: 0 0 8px;
  color: #c0392b;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.how-it-works-back:hover {
  text-decoration: underline;
}
/* A chooser item that offers BOTH a live tour and a recorded video shows
   explicit action buttons instead of a single whole-row click. */
.how-it-works-choice.is-static {
  cursor: default;
}
.how-it-works-choice.is-static:hover {
  background: #fff;
  transform: none;
  box-shadow: none;
}
.how-it-works-choice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.hiw-act {
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 700;
  padding: 7px 14px;
  cursor: pointer;
}
.hiw-act-primary {
  background: #c0392b;
  color: #fff;
}
.hiw-act-primary:hover {
  background: #a02b1f;
}
.hiw-act-ghost {
  background: #fff;
  color: #555;
  border-color: #d6d6d6;
}
.hiw-act-ghost:hover {
  background: #f5f5f5;
  color: #333;
}
.hiw-act-rec {
  background: #1f1f1f;
  color: #fff;
}
.hiw-act-rec:hover {
  background: #000;
}
.hiw-act-del {
  background: #fff;
  color: #c0392b;
  border-color: #e2b8b3;
}
.hiw-act-del:hover {
  background: #c0392b;
  color: #fff;
}
</style>
